import DataManagerIcon from '../../core/icons/DataManager';
import VocabularyManagerIcon from '../../core/icons/VocabularyManager';
import OntologyManagerIcon from '../../core/icons/OntologyManager';
import { APPLICATION_PATHS } from '../../application-core/constants';
import GrainIcon from '@material-ui/icons/Grain';
import ReorderIcon from '@material-ui/icons/Reorder';
import OntologyIcon from '../../core/icons/Ontology';
import DatasetIcon from '../../core/icons/Dataset';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import GS from '../../grafsync/grafsync';
import { DATATYPES, LANGS, DATATYPE_LIMITS, DATATYPE_LABELS, DEFAULT_LANG, STATUS_ACCEPTED, STATUS_REJECTED, BN_NAMESPACE, RTL_REGEX, LTR_SCRIPTS, RTL_SCRIPTS } from './constants';
import MoodIcon from '@material-ui/icons/Mood';
import MoodBadIcon from '@material-ui/icons/MoodBad';
import ErrorIcon from '@material-ui/icons/Error';
import Deprecated from '../../core/Deprecated';
import XL from '../../vocabulary/core/XL';
import Tooltip from '@material-ui/core/Tooltip';

const OBO_NAMESPACE = "http://purl.obolibrary.org/obo/";

export function getAllLabelValues(data, lang) {
  const prefLabels = data.prefLabel && data.prefLabel[lang] ? [data.prefLabel[lang]] : [];
  const altLabels = data.altLabel && data.altLabel[lang] ?
    Array.isArray(data.altLabel[lang]) ?
      data.altLabel[lang]
      :
      [data.altLabel[lang]]
    :
    [];
  const hiddenLabels = data.hiddenLabel && data.hiddenLabel[lang] ?
    Array.isArray(data.hiddenLabel[lang]) ?
      data.hiddenLabel[lang]
      :
      [data.hiddenLabel[lang]]
    :
    [];
  const allLabels = [...prefLabels, ...altLabels, ...hiddenLabels];
  return allLabels;
}

export function getArrayValues(data, property, sort = true) {
  if (!(property in data)) return [];
  if (data[property] === undefined) return [];
  const values = Array.isArray(data[property]) ?
    [...data[property]]
    :
    [data[property]];
  return sort ? values.sort() : values;
}

export function computeTypeFromType(resourceType) {
  const dataTypes = Array.isArray(resourceType) ? resourceType : [resourceType];
  let computedType;
  if (dataTypes.includes("Resource")) computedType = "https://grafsync.graphifi.com/schema/gs/Resource";
  else if (dataTypes.includes("Concept")) computedType = "http://www.w3.org/2004/02/skos/core#Concept";
  else if (dataTypes.includes("Label")) computedType = "http://www.w3.org/2008/05/skos-xl#Label";
  else if (dataTypes.includes("OwlClass")) computedType = "http://www.w3.org/2002/07/owl#Class";
  else if (dataTypes.includes("ObjectProperty")) computedType = "http://www.w3.org/2002/07/owl#ObjectProperty";
  else if (dataTypes.includes("DatatypeProperty")) computedType = "http://www.w3.org/2002/07/owl#DatatypeProperty";
  else if (dataTypes.includes("AnnotationProperty")) computedType = "http://www.w3.org/2002/07/owl#AnnotationProperty";
  else if (dataTypes.includes("NamedIndividual")) computedType = "http://www.w3.org/2002/07/owl#NamedIndividual";
  else if (dataTypes.includes("RdfsDatatype")) computedType = "http://www.w3.org/2000/01/rdf-schema#Datatype";
  return computedType;
}

export function computeLabelFromType(resourceType) {
  const dataTypes = Array.isArray(resourceType) ? resourceType : [resourceType];
  let label = 'Unknown';
  if (dataTypes.includes("Resource")) label = "Resource";
  else if (dataTypes.includes("Dataset")) label = "Dataset";
  else if (dataTypes.includes("Concept")) label = "Concept";
  else if (dataTypes.includes("ConceptScheme")) label = "Taxonomy";
  else if (dataTypes.includes("Collection")) label = "Collection";
  else if (dataTypes.includes("OrderedCollection")) label = "Ordered Collection";
  else if (dataTypes.includes("Label")) label = "SKOS-XL Label";
  else if (dataTypes.includes("Ontology")) label = "Ontology";
  else if (dataTypes.includes("OwlClass")) label = "Class";
  else if (dataTypes.includes("ObjectProperty")) label = "Object Property";
  else if (dataTypes.includes("DatatypeProperty")) label = "Datatype Property";
  else if (dataTypes.includes("AnnotationProperty")) label = "Annotation Property";
  else if (dataTypes.includes("NamedIndividual")) label = "Named Individual";
  else if (dataTypes.includes("RdfsDatatype")) label = "Datatype";
  else if (dataTypes.includes("User")) label = "User";
  return label;
}

export function computeLinkFromType(resourceType, resourceIRI, componentIRI, projectIRI) {
  let link = ["Collection", "OrderedCollection"].includes(resourceType) ?
    '/project/collection#project=' + projectIRI + "&collection=" + componentIRI
    :
    ["ConceptScheme", "Label", "Concept"].includes(resourceType) ?
      '/project/taxonomy#project=' + projectIRI + "&taxonomy=" + componentIRI
      :
      ["Dataset", "Resource"].includes(resourceType) ?
        '/project/dataset#project=' + projectIRI + "&dataset=" + componentIRI
        :
        '/project/ontology#project=' + projectIRI + "&ontology=" + componentIRI;
  if (resourceType === "Resource") link += "&resource=" + resourceIRI;
  if (resourceType === "Concept") link += "&view=concept&concept=" + resourceIRI;
  if (resourceType === "Label") link += "&view=label&label=" + resourceIRI;
  if (resourceType === "OwlClass") link += "&view=class&class=" + resourceIRI;
  if (resourceType === "ObjectProperty") link += "&view=objectProperty&objectProperty=" + resourceIRI;
  if (resourceType === "DatatypeProperty") link += "&view=datatypeProperty&datatypeProperty=" + resourceIRI;
  if (resourceType === "AnnotationProperty") link += "&view=annotationProperty&annotationProperty=" + resourceIRI;
  if (resourceType === "NamedIndividual") link += "&view=namedIndividual&namedIndividual=" + resourceIRI;
  if (resourceType === "RdfsDatatype") link += "&view=datatype&datatype=" + resourceIRI;
  return link;
}

export function getComponents(data, includeIsDefinedBy = true, favourVisibility = false) {
  let components = [];
  if (data.visibility && data.visibility.length > 0) components = Array.isArray(data.visibility) ?
    data.visibility
    :
    [data.visibility];
  if (favourVisibility && components.length > 0) {
    return components.sort();
  }
  const isDefinedBy = includeIsDefinedBy && data.isDefinedBy && data.isDefinedBy.length > 0 ?
    Array.isArray(data.isDefinedBy) ?
      data.isDefinedBy
      :
      [data.isDefinedBy]
    :
    [];
  components = [...new Set(components.concat(isDefinedBy))];
  return components.sort();
}

export function computeApplicationPathFromType(resourceType) {
  return ["ConceptScheme", "Concept", "Label", "Collection", "OrderedCollection"].includes(resourceType) ?
    APPLICATION_PATHS.TAXONOMY_MANAGER
    :
    ["Dataset", "Resource"].includes(resourceType) ?
      APPLICATION_PATHS.DATA_MANAGER
      :
      APPLICATION_PATHS.ONTOLOGY_MANAGER;
}

export function computeProjectIcon(projectType, style = { color: 'primary' }) {
  const icon = projectType === "TaxonomyProject" ?
    <VocabularyManagerIcon style={style} />
    :
    projectType === "DataProject" ?
      <DataManagerIcon style={style} />
      :
      <OntologyManagerIcon style={style} />;
  return icon;

}

export function computeComponentIcon(componentType, style = { color: '#808080' }) {
  const dataTypes = Array.isArray(componentType) ? componentType : [componentType];
  const icon = dataTypes.includes("OrderedCollection") ?
    <ReorderIcon data-test="ordered-icon" fontSize="small" style={style} />
    :
    dataTypes.includes("Collection") ?
      <GrainIcon data-test="unordered-icon" fontSize="small" style={style} />
      :
      dataTypes.includes("Dataset") ?
        <DatasetIcon data-test="dataset-icon" fontSize="small" style={style} />
        :
        dataTypes.includes("ConceptScheme") ?
          <BubbleChartIcon data-test="scheme-icon" fontSize="small" style={style} />
          :
          dataTypes.includes("Ontology") ?
            <OntologyIcon data-test="ontology-icon" fontSize="small" style={style} iconColor={style.color} />
            :
            <ErrorIcon data-test="unknown-icon" fontSize="small" style={style} />;
  return icon;
}

export function computeConceptIcon(data, useWorkflow = false) {
  let icon;
  if (data.deprecated) {
    icon = <Deprecated />;
  }
  else if (useWorkflow && "status" in data && data.status[0] && data.status[0] === STATUS_ACCEPTED) {
    icon = <Tooltip title="Approved">
      <span data-test="approved-icon"
        style={{
          textAlign: 'center',
          display: 'inline-block',
          paddingTop: '4px'
        }}>
        <MoodIcon fontSize="small" />
      </span>
    </Tooltip>;
  }
  else if (useWorkflow && "status" in data && data.status[0] && data.status[0] === STATUS_REJECTED) {
    icon = <Tooltip title="Rejected">
      <span data-test="rejected-icon"
        style={{
          textAlign: 'center',
          display: 'inline-block',
          paddingTop: '4px'
        }}>
        <MoodBadIcon fontSize="small" style={{ color: '#DD2C00' }} />
      </span>
    </Tooltip>;
  }
  else if (data.isEntailed) {
    icon = <XL />;
  }
  return icon;
}

// isDefinedBy needs to match the baseIRI of the ontology IRI minus any trailing hash or slash
// or the resource IRI needs to match the baseIRI
export function isInOntology(resourceIRI, isDefinedBy, ontologyIRI) {
  let baseIRI = ontologyIRI;
  if (baseIRI.endsWith('#')) baseIRI = baseIRI.substring(0, baseIRI.length - 1);
  if (baseIRI.endsWith('/')) baseIRI = baseIRI.substring(0, baseIRI.length - 1);
  if (resourceIRI.indexOf(baseIRI) === 0) return true;
  // OBO? These will all have visibility property now
  if (ontologyIRI.startsWith(OBO_NAMESPACE) && resourceIRI.startsWith(OBO_NAMESPACE)) {
    if (resourceIRI.indexOf("_") > 0) {
      const oboSegment = resourceIRI.substring(0, resourceIRI.indexOf("_")).toLowerCase();
      if (ontologyIRI.startsWith(oboSegment)) return true;
    }
    if (resourceIRI.indexOf("#") > 0) {
      const oboSegment = resourceIRI.substring(0, resourceIRI.indexOf("#")).toLowerCase();
      if (ontologyIRI.startsWith(oboSegment)) return true;
    }
  }
  if (!isDefinedBy) return false;
  if (typeof isDefinedBy !== "string") return false;
  if (isDefinedBy.indexOf(baseIRI) === 0) return true;
  return false;
}

export function isInContextComponent(data, componentIRI, contextProperty = "isDefinedBy") {
  if (contextProperty in data) {
    const visibleComponents = Array.isArray(data[contextProperty]) ? data[contextProperty] : [data[contextProperty]];
    if (visibleComponents.includes(componentIRI)) return true;
  }
  if ("visibility" in data) {
    const visibleComponents = Array.isArray(data.visibility) ? data.visibility : [data.visibility];
    if (visibleComponents.includes(componentIRI)) return true;
  }
  return false;
}

export function computeOntologyIRIoptions(ontologyIRI) {
  let baseIRI = ontologyIRI;
  if (baseIRI.endsWith('#')) baseIRI = baseIRI.substring(0, baseIRI.length - 1);
  if (baseIRI.endsWith('/')) baseIRI = baseIRI.substring(0, baseIRI.length - 1);
  return baseIRI;
}

export function validatePassword(password) {
  return (/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,50})/.test(password));
}

export function validateEmail(email) {
  if (!email.includes("@")) return false;
  if (!email.includes(".")) return false;
  return (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(email));
}

export function validateUsername(username) {
  return (/^[a-zA-Z0-9]+$/.test(username));
}

export function displayDate(dateTime, includeTime = false, utc = false) {
  // We're expecting a date time but just in case we get a date!...
  // Dates before 1900 can cause issues so if that is the case we do simple formatting
  if (dateTime["@value"]) {
    if (dateTime.type === "http://www.w3.org/2001/XMLSchema#date") {
      dateTime = dateTime["@value"];
    }
    else return 'Invalid date';
  }
  if (typeof dateTime === "string" && parseInt(dateTime.substring(0, 4)) < 1900) {
    return dateTime.replaceAll("-", "/");
  }
  const text = new Date(dateTime);
  let dateText = text.getDate() + " " + text.toLocaleString('default', { month: 'long' }) + " " + text.getFullYear();
  if (includeTime) {
    if (utc) {
      dateText += " at " + ("0" + text.getUTCHours()).slice(-2) + ":" +
        ("0" + text.getUTCMinutes()).slice(-2) + ":" +
        ("0" + text.getUTCSeconds()).slice(-2) + " (UTC)";
    }
    else {
      dateText += " at " + text.toLocaleTimeString([], { timeStyle: 'medium' });
    }
  }
  return dateText;
}

export function displayUser(users, user) {
  const userId = Array.isArray(user) ? user[0] : user;
  const userData = users.find(user => user.id === userId);
  const userText = userData && userData.givenName && userData.familyName ?
    userData.givenName + " " + userData.familyName
    :
    userData ?
      userData.username
      :
      "User not available";
  return userText;
}

export function debounce(func, context, args, delay = 500, iteration = 0, maxIterations = 40) {
  // Minimum 50ms
  if (delay < 50) delay = 50;
  if (context.hcTimeout) clearTimeout(context.hcTimeout);
  context.hcTimeout = setTimeout(() => {
    // If we're still searching wait a bit longer
    if (context.state.isSearching && iteration < maxIterations) {
      iteration++;
      debounce(func, context, args, delay, iteration, maxIterations);
    }
    else {
      func(...args);
    }
  }, delay);
}

/**
 * Check basic valus of a string parameter ar met
 * @param {*} param   The parameter to check
 */
export function checkStringParam(param) {
  if (!param || param === undefined || param === "" || typeof param !== "string") return false;
  return true;
}

export function checkParam(param) {
  if (param === 0) return true;
  if (!param || param === undefined || param === "") return false;
  return true;
}

/**
 * Convert a project IRI into its equivalent graph IRI
 * @param {*} projectIRI  The IRI of a project
 */
export function getGraphFromProject(projectIRI) {
  if (!checkStringParam(projectIRI)) throw new Error("An IRI must be supplied to retrieve a graph.");
  if (!checkIRI(projectIRI)) throw new Error("An invalid project IRI has been supplied for graph computation.");
  return "https://grafsync.graphifi.com/graph/project/" + projectIRI.substring(projectIRI.indexOf('/data/project') + 14);
}

export function getProjectFromGraph(graphIRI) {
  if (!checkStringParam(graphIRI)) throw new Error("An IRI must be supplied to retrieve a project.");
  if (!checkIRI(graphIRI)) throw new Error("An invalid graph IRI has been supplied for project computation.");
  return "https://grafsync.graphifi.com/data" + graphIRI.substring(graphIRI.indexOf("/graph") + 6);
}

/**
 * Check if an iri is a valid IRI. We'll keep working on improving this
 * @param {*} iri         An IRI
 * @param {*} allowIPaddress  Indicates if IP addresses should be allowed in check
 */
export function checkIRI(iri, allowIPaddress = false) {
  if (!checkStringParam(iri)) return false;
  if (iri.indexOf(" ") >= 0) return false;
  // Backslash not permitted
  if (iri.indexOf("\\") >= 0) return false;
  // Square brackets not supported
  if (iri.indexOf("[") >= 0) return false;
  if (iri.indexOf("]") >= 0) return false;
  // Double quotes not supported
  if (iri.indexOf('"') >= 0) return false;
  // Braces not supported
  if (iri.indexOf("{") >= 0) return false;
  if (iri.indexOf("}") >= 0) return false;
  if (iri.indexOf("`") >= 0) return false;
  if (iri.indexOf("^") >= 0) return false;
  if (iri.indexOf("~") >= 0) return false;
  if (iri.indexOf("|") >= 0) return false;
  if (iri.indexOf("@") >= 0) return false;
  if (iri.indexOf("<") >= 0) return false;
  if (iri.indexOf(">") >= 0) return false;
  // Do not allow RTL text in IRIs as it causes a lot of headaches!
  if (iri.match(RTL_REGEX)) {
    return false;
  }
  // Don't allow upper case protocol even though technically allowed
  if (!(iri.startsWith("http:") || iri.startsWith("https:"))) return false;
  try {
    // If invalid for whatever reason return false (might be partially escaped char for instance)
    decodeURI(iri);
    const url = new URL(iri);
    if (!url.host.includes('.')) return false;
    if (url.hostname.substring(url.hostname.length - 2).includes('.')) return false;
    if (url.hash && url.hash.substring(1).includes('#')) return false;
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  }
  catch (e) {
    return false;
  }
  const isIPaddressRegex = RegExp("^https?:\\/\\/[[0-9]");
  let isIPaddress = isIPaddressRegex.test(iri);
  if (isIPaddress) {
    if (!allowIPaddress) return false;
    const IPregex = RegExp("^https?:\\/\\/(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\S*$");
    return IPregex.test(iri);
  }
  return true;
}

export function basicCheckIRI(iri) {
  if (!checkStringParam(iri)) return false;
  try {
    // If invalid for whatever reason return false (might be partially escaped char for instance)
    decodeURI(iri);
    const url = new URL(iri);
    if (!url.host.includes('.')) return false;
    if (url.hostname.substring(url.hostname.length - 2).includes('.')) return false;
    if (url.hash && url.hash.substring(1).includes('#')) return false;
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  }
  catch (e) {
    return false;
  }
  return true;
}

export function detailedCheckIRI(iri) {
  if (!checkStringParam(iri)) return false;
  if (iri.indexOf(" ") >= 0) return false;
  // Backslash not permitted
  if (iri.indexOf("\\") >= 0) return false;
  // Square brackets not supported
  if (iri.indexOf("[") >= 0) return false;
  if (iri.indexOf("]") >= 0) return false;
  // Double quotes not supported
  if (iri.indexOf('"') >= 0) return false;
  // Braces not supported
  if (iri.indexOf("{") >= 0) return false;
  if (iri.indexOf("}") >= 0) return false;
  if (iri.indexOf("`") >= 0) return false;
  if (iri.indexOf("^") >= 0) return false;
  if (iri.indexOf("~") >= 0) return false;
  if (iri.indexOf("|") >= 0) return false;
  if (iri.indexOf("@") >= 0) return false;
  if (iri.indexOf("<") >= 0) return false;
  if (iri.indexOf(">") >= 0) return false;
  if ((iri.indexOf('#') > 0 && (iri.indexOf('#') !== iri.lastIndexOf('#')))
    || iri.toLowerCase().includes('graphifi')
    || iri.toLowerCase().includes('grafsync')) return false;
  // Don't allow upper case protocol even though technically allowed
  if (!(iri.startsWith("http:") || iri.startsWith("https:"))) return false;
  // Do not allow RTL text in IRIs as it causes a lot of headaches!
  if (iri.match(RTL_REGEX)) {
    return false;
  }
  try {
    // If invalid for whatever reason return false (might be partially escaped char for instance)
    decodeURI(iri);
    const url = new URL(iri);
    if (!url.host.includes('.')) return false;
    if (url.hostname.substring(url.hostname.length - 2).includes('.')) return false;
    if (url.hash && url.hash.substring(1).includes('#')) return false;
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  }
  catch (e) {
    return false;
  }
  return true;
}

export function isSupportedDatatype(datatype) {
  return Object.keys(DATATYPES).some(key => DATATYPES[key] === datatype);
}

export function getDatatypeMenuOptionsByIRI(exclusionList = []) {
  const flatExclustionList = exclusionList.flat();
  let menuOptions = Object.keys(DATATYPES)
    .filter(key => !flatExclustionList.includes(DATATYPES[key]))
    .map(datatype => {
      let label = DATATYPE_LABELS[DATATYPES[datatype]][DEFAULT_LANG];
      if (label === "NN Integer") label = "Non-Negative Integer";
      return {
        value: DATATYPES[datatype],
        label
      };
    });
  menuOptions.push({
    value: "http://www.w3.org/1999/02/22-rdf-syntax-ns#PlainLiteral",
    label: "Plain Literal"
  });
  menuOptions.sort(function (a, b) {
    return a.label < b.label ? -1 : 0;
  });
  return menuOptions;
}

export function getDatatypeMenuOptions(exclusionList = []) {
  const flatExclustionList = exclusionList.flat();
  const menuOptions = Object.keys(DATATYPES)
    .filter(key => !flatExclustionList.includes(key))
    .map(datatype => {
      let label = DATATYPE_LABELS[DATATYPES[datatype]][DEFAULT_LANG];
      if (label === "NN Integer") label = "Non-Negative Integer";
      return {
        value: DATATYPES[datatype],
        label
      };
    })
    .sort(function (a, b) {
      return a.label < b.label ? -1 : 0;
    });
  return menuOptions;
}

/**
 * Given a string of text compute its datatype if possible
 * @param {*} text  The text to compute a datatype for
 */
export function computeDatatype(text) {
  if (!checkParam(text)) throw new Error("A string must be supplied to compute its datatype.");
  if (typeof text === "boolean") {
    return DATATYPES.BOOLEAN;
  }
  // If it looks likes a number and the existing value is compatible map. We only look fo decimal for now
  else if (typeof text === "number") {
    if (text.toString().indexOf(".") > 0) {
      return DATATYPES.DECIMAL;
    }
    else return DATATYPES.INTEGER;
  }
  return null;
}

/**
 * Check if a string of text matches against a supplied datatype
 * @param {*} text      The text to be validated
 * @param {*} datatype  The IRI of the datatype to check for
 */
export function checkValidForDatatype(text, datatype) {
  if (!checkParam(text)) throw new Error("A string must be supplied for a datatype check.");
  if (!datatype) return true;
  if (DATATYPE_LIMITS[datatype]) {
    const dtInfo = DATATYPE_LIMITS[datatype];
    const regex = dtInfo.expression;
    if (regex) {
      const datatypeRegex = RegExp(regex);
      let isValid = datatypeRegex.test(text);
      // Now we check the value space constraints if any
      if (isValid) {
        if (dtInfo.minimumValue && Number(text) < Number(dtInfo.minimumValue)) isValid = false;
        if (dtInfo.maximumValue && Number(text) > Number(dtInfo.maximumValue)) isValid = false;
      }
      return isValid;
    }
    return true;
  }
  return true;
}


/**
 * 
 * @param {*} text 
 * @param {*} datatype 
 * @param {*} existingValues Will be an array of values or objects of form {text, datatype}
 * @param {*} suppliedMaxLength 
 */
export function checkValidity(text, datatype, existingValues, suppliedMaxLength) {
  let isValid = true;
  // First to a straight lexical comparison (including datatype if applicable)
  let existing = existingValues && existingValues.some(value => {
    if (typeof value === "object") {
      return text === value.text.toString() && (!datatype || datatype === value.datatype);
    }
    else return text === value.toString();
  });
  // If none found there compare value space values if there is a datatype which is numerical in nature
  // This would not match URIs with the same value space value
  if (!existing && datatype) {
    const valueSpaceText = DATATYPE_LIMITS[datatype] && DATATYPE_LIMITS[datatype].maxLength ? Number(text) : null;
    if (valueSpaceText === 0 || valueSpaceText) {
      // Don't allow 00
      if (valueSpaceText === 0 && text !== "0") return false;
      // Don't allow leading zeroes on numbers
      if (valueSpaceText !== 0 && text.charAt(0) === "0") return false;
      existing = existingValues && existingValues.some(value => {
        if (typeof value === "object") {
          return valueSpaceText === Number(value.text.toString()) && datatype === value.datatype;
        }
        else return valueSpaceText === Number(value.toString());
      });
    }
  }
  if (isValid) isValid = checkValidForDatatype(text, datatype);
  const maxLength = datatype && DATATYPE_LIMITS[datatype] && DATATYPE_LIMITS[datatype].maxLength ?
    DATATYPE_LIMITS[datatype].maxLength
    :
    suppliedMaxLength;
  if (text.length > maxLength) isValid = false;
  return { isValid, existing };
}

/**
 * Check if a string is at the maximum length and generate a warning.
 * @param {*} text    The string to check
 * @param {*} value   The length to check for
 */
export function createLengthWarning(text, value, isEdited = true) {
  if (text.length >= value && isEdited) return <span data-test="helper-text">Maximum is {value} characters.</span>;
  return null;
}

export function createLengthWarning2(text, value, isEdited = true) {
  if ((text.length >= value && isEdited) || (text.length > value && !isEdited)) return <span data-test="helper-text">Maximum is {value} characters.</span>;
  return null;
}

export function checkEntitlements(userPermissions, needed) {
  const isPermitted = userPermissions.some(userEnt => needed.includes(userEnt)) || false;
  return isPermitted;
}

// This is not used
export function checkAccessEntitlements(userPermissions, needed) {
  if (!userPermissions) return false;
  const isPermitted = userPermissions.every(userEnt => needed.includes(userEnt)) || false;
  return isPermitted;
}

// The entitlements on a resource will be project specific
// Note if a resource is newly created it won't have hasEntitlement as only gets added on loading
// But assucmption is that if you can create it you can edit it.
export function checkResourceEntitlements(resource, needed) {
  if (GS.isReadOnly()) return true;
  // If about to be deleted it is read only
  if (resource.pendingDeletion) return true;
  // If no entitlement information by default writeable (i.e. created by user)
  if (!resource.hasEntitlement) return false;
  const neededPermissions = Array.isArray(needed) ? needed : [needed];
  const isPermitted = resource.hasEntitlement.some(userEnt => neededPermissions.includes(userEnt)) || false;
  return !isPermitted;
}

// Expect an array of language codes and get back an array of langcode + lang in required language
export function getOrderedLanguages(langCodes, lang) {
  const subset = langCodes.map(code => {
    return { code: code, label: LANGS[code][lang] };
  });
  return sortByProp(subset, "label");
}

export function getAllOrderedLanguages(lang) {
  const subset = Object.keys(LANGS).map(code => {
    return { code: code, label: LANGS[code][lang] };
  });
  return sortByProp(subset, "label");
}

export function getAllLanguageCodes() {
  return Object.keys(LANGS);
}

export function isRTL(lang) {
  if (!lang) return false;
  if (LANGS[lang]) return LANGS[lang].rtl || false;
  // If the code contains an RTL script then treat as RTL
  const hasLTRScript = LTR_SCRIPTS.some(rtls => lang.includes("-" + rtls));
  if (hasLTRScript) return false;
  // If the code contains an RTL script then treat as RTL
  const hasRTLScript = RTL_SCRIPTS.some(rtls => lang.includes("-" + rtls));
  if (hasRTLScript) return true;
  // If the langauge code starts with a two letter code that is RTL treat as RTL
  if (lang.charAt(2) === "-" && LANGS[lang.substring(0, 2)].rtl) return true;
  return false;
}

export function processHashLocation(location) {

  const paramsMap = new Map();
  const projectParamsText = location.hash.substring(1);
  const projectParamsList = projectParamsText.split('&');
  if (projectParamsList) {
    projectParamsList.forEach(text => {
      const equalsIndex = text.indexOf("=");
      const keyText = text.substring(0, equalsIndex);
      const keyValue = text.substring(equalsIndex + 1);
      paramsMap.set(keyText, keyValue);
    });
  }
  return paramsMap;
}

export function sortObjects(objects, property, lang = "en", secondLang, caseSensitive = true) {
  const now = Date.now();
  return objects.sort(function (a, b) {
    let orderA = a.id.includes('#') ?
      decodeURIComponent(a.id.substring(a.id.lastIndexOf('#') + 1))
      :
      decodeURIComponent(a.id.substring(a.id.lastIndexOf('/') + 1));
    let orderB = b.id.includes('#') ?
      decodeURIComponent(b.id.substring(b.id.lastIndexOf('#') + 1))
      :
      decodeURIComponent(b.id.substring(b.id.lastIndexOf('/') + 1));
    if (a[property]) {
      a[property][lang] === undefined ? orderA = a.id : orderA = a[property][lang];
      if (orderA === a.id && secondLang) {
        a[property][secondLang] === undefined ? orderA = a.id : orderA = a[property][secondLang];
        if (orderA === a.id) {
          a[property]["und"] === undefined ? orderA = a.id : orderA = a[property]["und"];
        }
      }
    }
    else if (a.id.startsWith(BN_NAMESPACE)) {
      const objectDate = a.created || a._created || Date.now();
      if (objectDate) {
        const ago = now - new Date(objectDate);
        orderA = " " + ago.toString().padStart(10, '0');
      }
      else orderA = " " + orderA;
    }
    if (b[property]) {
      b[property][lang] === undefined ? orderB = b.id : orderB = b[property][lang];
      if (orderB === b.id && secondLang) {
        b[property][secondLang] === undefined ? orderB = b.id : orderB = b[property][secondLang];
        if (orderB === b.id) {
          b[property]["und"] === undefined ? orderB = b.id : orderB = b[property]["und"];
        }
      }
    }
    else if (b.id.startsWith(BN_NAMESPACE)) {
      const objectDate = b.created || b._created;
      if (objectDate) {
        const ago = now - new Date(objectDate);
        orderB = " " + ago.toString().padStart(10, '0');
      }
      else orderB = " " + orderB;
    }
    if (Array.isArray(orderA)) orderA = orderA.sort().join("");
    if (Array.isArray(orderB)) orderB = orderB.sort().join("");
    if (caseSensitive) {
      if (orderA < orderB) return -1;
      if (orderA > orderB) return 1;
    }
    else {
      if (orderA.toLowerCase() < orderB.toLowerCase()) return -1;
      if (orderA.toLowerCase() > orderB.toLowerCase()) return 1;
    }
    // All still being equal order by URI
    if (a.id < b.id) return - 1;
    if (a.id > b.id) return 1;
    return 0;
  });
}

// Ideally we would sort ignoring accents and this function will do it in Javascript
// Unfortunately we can't really use this becaue SPARQL doesn't have an equivalent means to sort!!!
/*function normalizeString(text) {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}*/

export function sortByLength(objects, property, lang = "en") {
  return objects.sort(function (a, b) {
    let orderA = 0;
    let orderB = 0;
    if (a[property]) {
      a[property][lang] === undefined ? orderA = 0 : orderA = a[property][lang].length;
    }
    if (b[property]) {
      b[property][lang] === undefined ? orderB = 0 : orderB = b[property][lang].length;
    }
    if (orderA < orderB) return -1;
    if (orderA > orderB) return 1;
    let orderA1 = a[property] && a[property][lang] ? a[property][lang] : "";
    let orderB1 = b[property] && b[property][lang] ? b[property][lang] : "";
    if (orderA1 < orderB1) return -1;
    if (orderA1 > orderB1) return 1;
    return 0;
  });
}

export function sortByProp(objects, property, secondProperty, caseSensitive = true) {
  return objects.sort(function (a, b) {
    let orderA = "";
    let orderB = "";
    a[property] === undefined ? orderA = "" : orderA = a[property];
    b[property] === undefined ? orderB = "" : orderB = b[property];
    if (caseSensitive) {
      if (orderA < orderB) return -1;
      if (orderA > orderB) return 1;
    }
    else {
      if (orderA.toLowerCase() < orderB.toLowerCase()) return -1;
      if (orderA.toLowerCase() > orderB.toLowerCase()) return 1;
    }
    if (orderA === orderB && secondProperty) {
      a[secondProperty] === undefined ? orderA = "" : orderA = a[secondProperty];
      b[secondProperty] === undefined ? orderB = "" : orderB = b[secondProperty];
      if (orderA < orderB) return -1;
      if (orderA > orderB) return 1;
    }
    return 0;
  });
}

export function computeNumericSortValue(value) {
  let orderValue = 0;
  if (value !== undefined) orderValue = Array.isArray(value) ?
    value.length === 0 ?
      0
      :
      value.map(v => typeof v === "number" ?
        v
        :
        typeof v === "string" || typeof v === "boolean" ?
          Number(v)
          :
          "@value" in v ?
            Number(v["@value"])
            :
            v).sort()[0]
    :
    typeof value === "number" ?
      value
      :
      typeof value === "string" || typeof value === "boolean" ?
        Number(value)
        :
        "@value" in value ?
          Number(value["@value"])
          :
          Number(value);
  if (isNaN(orderValue)) orderValue = undefined;
  return orderValue;
}

export function computeStringSortValue(value, lang) {
  let orderValue = "";
  if (value !== undefined) orderValue = Array.isArray(value) ?
    value.length === 0 ?
      ""
      :
      value
        .filter(v => v.language ? v.language === lang : true)
        .map(v => typeof v === "number" || typeof v === "boolean" ?
          v.toString()
          :
          typeof v === "string" ?
            v
            :
            "@value" in v ?
              v["@value"]
              :
              "id" in v ?
                v["id"]
                :
                v).sort()[0]
    :
    typeof value === "number" || typeof value === "boolean" ?
      value.toString()
      :
      typeof value === "string" ?
        value
        :
        "@value" in value ?
          "language" in value ?
            value.language === lang ?
              value["@value"]
              :
              undefined
            :
            value["@value"]
          :
          "id" in value ?
            value["id"]
            :
            value.toString();
  return orderValue;
}

export function sortByAnyProp(objects, property, numeric, lang = "en") {
  return objects.sort(function (a, b) {
    let orderA = "";
    let orderB = "";
    if (numeric) {
      // For arrays take lowest value
      orderA = computeNumericSortValue(a[property]);
      orderB = computeNumericSortValue(b[property]);
      if (orderA === undefined) orderA = 0;
      if (orderB === undefined) orderB = 0;
      if (orderA < orderB) return -1;
      if (orderA > orderB) return 1;
    }
    else {
      orderA = computeStringSortValue(a[property], lang);
      orderB = computeStringSortValue(b[property], lang);
      if (orderA === undefined) orderA = "";
      if (orderB === undefined) orderB = "";
      if (orderA.toLowerCase() < orderB.toLowerCase()) return -1;
      if (orderA.toLowerCase() > orderB.toLowerCase()) return 1;
    }
    return 0;
  });
}

export function reverseSortByProp(objects, property) {
  return objects.sort(function (a, b) {
    let orderA = "";
    let orderB = "";
    a[property] === undefined ? orderA = "" : orderA = a[property];
    b[property] === undefined ? orderB = "" : orderB = b[property];
    if (orderA < orderB) return 1;
    if (orderA > orderB) return -1;
    return 0;
  });
}

// A common method to sort a set of IRIs where the data is loaded into GS where we can sort on an array of possible labels
// A single graph can be provided or a list that maps against the resource list
export const sortResourcesByLangPropList = (resources, graphOrList, propertyList, lang = "en", secondLang) => {
  const dataFromResources = resources.map((r, index) => {
    const graph = Array.isArray(graphOrList) ? graphOrList[index] : graphOrList;
    const data = GS.data(r, graph, false);
    if (data && data.id) {
      return data;
    }
    else {
      return {
        id: r
      };
    }
  });
  const sortedResources = sortByLangPropList(dataFromResources, propertyList, lang, secondLang);
  const sortedResourceValues = sortedResources.map(r => r.id);
  return sortedResourceValues;
};

export function sortByLangPropList(objects, propertyList, lang = "en", secondLang) {
  return objects.sort(function (a, b) {
    let orderA = a.id;
    let orderB = b.id;
    let aProperty = propertyList[0];
    let bProperty = propertyList[0];
    for (let i = 0; i < propertyList.length; i++) {
      aProperty = propertyList[i];
      if (aProperty in a) break;
    }
    for (let i = 0; i < propertyList.length; i++) {
      bProperty = propertyList[i];
      if (bProperty in b) break;
    }
    let aLangToUse = lang;
    if (a[aProperty] && !a[aProperty][lang]) {
      aLangToUse = secondLang;
      if (!a[aProperty][secondLang]) aLangToUse = "und";
    }
    let bLangToUse = lang;
    if (b[bProperty] && !b[bProperty][lang]) {
      bLangToUse = secondLang;
      if (!b[bProperty][secondLang]) bLangToUse = "und";
    }
    orderA = a[aProperty] !== undefined && a[aProperty][aLangToUse] ?
      a[aProperty][aLangToUse]
      :
      a.id;
    orderB = b[bProperty] !== undefined && b[bProperty][bLangToUse] ?
      b[bProperty][bLangToUse]
      :
      b.id;
    if (Array.isArray(orderA)) orderA = orderA.sort().join("");
    if (Array.isArray(orderB)) orderB = orderB.sort().join("");
    if (orderA.toLowerCase() < orderB.toLowerCase()) return -1;
    if (orderA.toLowerCase() > orderB.toLowerCase()) return 1;
    return 0;
  });
}

export const getLabelOrID = (iri, data, prop, lang) => {
  if (!data) return iri;
  if (lang) {
    const label = data && data[prop] ?
      data[prop][lang] ?
        Array.isArray(data[prop][lang]) ?
          data[prop][lang].sort().join()
          :
          data[prop][lang]
        :
        data[prop]["und"] ?
          Array.isArray(data[prop]["und"]) ?
            data[prop]["und"].sort().join()
            :
            data[prop]["und"]
          :
          data.id
      :
      data.id;
    return label;
  }
  else {
    const label = data && data[prop] ?
      Array.isArray(data[prop]) ?
        data[prop].sort().join()
        :
        data[prop]
      :
      data.id;
    return label;
  }
};

export const getLabelDisplayLang = (data, viewLang, defaultLang, label = "label") => {
  if (!(label in data)) return defaultLang;
  return getLabelDisplayLangFromLabel(data[label], viewLang, defaultLang);
};

// If we have say en-us we will fallback to en if available
// If we have say en we will fallback to en-us (or the like) if available
export const getLabelDisplayLangFromLabel = (label, viewLang, defaultLang) => {
  if (!label) return defaultLang;
  if (viewLang && label[viewLang]) {
    return viewLang;
  }
  else if (label[defaultLang]) {
    return defaultLang;
  }
  else if (label["und"]) {
    return "und";
  }
  else {
    if (defaultLang.includes("-")) {
      const mainLanguage = defaultLang.split("-")[0];
      const langKeys = Object.keys(label).sort();
      const foundMain = langKeys.some(lk => lk === mainLanguage);
      if (foundMain) return mainLanguage;
    }
    else {
      const langKeys = Object.keys(label).sort();
      const sublanguage = langKeys.find(lk => lk.startsWith(viewLang + "-"));
      if (sublanguage) return sublanguage;
      if (Object.keys(label).length === 1) {
        // Otherwise just return what there is if only one language
        return Object.keys(label)[0];
      }
    }
  }
  return defaultLang;
};

export const getLabelText = (data, displayLang, label = "label", defaultText) => {
  return label in data && data[label][displayLang] ?
    Array.isArray(data[label][displayLang]) ?
      [...data[label][displayLang]].sort().join(" AND ")
      :
      data[label][displayLang]
    :
    defaultText ?
      defaultText
      :
      data.id;
};

export const getLabelTextFromLabel = (label, id, displayLang, defaultText) => {
  return label && label[displayLang] ?
    Array.isArray(label[displayLang]) ?
      [...label[displayLang]].sort().join(" AND ")
      :
      label[displayLang]
    :
    defaultText ?
      defaultText
      :
      id;
};

export const getResourceLabel = (data, prop, lang, includeLang = true) => {
  const label = data && data[prop] ?
    data[prop][lang] ?
      Array.isArray(data[prop][lang]) ?
        data[prop][lang].join(" AND ")
        :
        data[prop][lang]
      :
      data[prop]["und"] ?
        Array.isArray(data[prop]["und"]) ?
          <span>
            {data[prop]["und"].join(" AND ")}
            {includeLang &&
              <span
                data-test={"language-code-" + lang}
                style={
                  { marginLeft: 12, fontSize: 12, borderRadius: '16px', backgroundColor: '#01579B', color: 'white', padding: '4px 8px 4px 8px' }
                }>No Language</span>
            }
          </span>
          :
          <span>
            {data[prop]["und"]}
            {includeLang &&
              <span
                data-test={"language-code-" + lang}
                style={
                  { marginLeft: 12, fontSize: 12, borderRadius: '16px', backgroundColor: '#01579B', color: 'white', padding: '4px 8px 4px 8px' }
                }>No Language</span>
            }
          </span>
        :
        data.id
    :
    data.id;
  return label;
};

// A common method to sort a set of IRIs where the data is loaded into GS
export const sortResources = (resources, graph, prop, lang) => {
  const sortedResources = [...resources].sort((a, b) => {
    const aData = GS.data(a, graph, false);
    const bData = GS.data(b, graph, false);
    const aLabel = getLabelOrID(a, aData, prop, lang);
    const bLabel = getLabelOrID(b, bData, prop, lang);
    if (aLabel.toLowerCase() < bLabel.toLowerCase()) return -1;
    if (bLabel.toLowerCase() < aLabel.toLowerCase()) return 1;
    return 0;
  });
  return sortedResources;
};

export const sortResourcesWithFallback = (resources, graph, prop, viewLang, defaultLang) => {
  const resourceData = resources.map(id => GS.data(id, graph));
  const firstLang = viewLang || defaultLang;
  const secondLang = defaultLang;
  // GS.data returns clones so we can sort on those object without affecting GS internal storage
  let sortedResources = sortObjects(resourceData, prop, firstLang, secondLang, false)
    .map(resource => resource.id);
  return sortedResources;
};

// A common method to sort a set of IRIs where the data is loaded into GS
export const sortByCreated = (resources, graph, ascending = true) => {
  const sortedResources = [...resources].sort((a, b) => {
    const aData = GS.data(a, graph, false);
    const bData = GS.data(b, graph, false);
    const aLabel = aData ? aData.created || aData._created || aData.id : a;
    const bLabel = bData ? bData.created || bData._created || bData.id : b;
    if (ascending) {
      if (aLabel.toLowerCase() < bLabel.toLowerCase()) return -1;
      if (bLabel.toLowerCase() < aLabel.toLowerCase()) return 1;
    }
    else {
      if (aLabel.toLowerCase() > bLabel.toLowerCase()) return -1;
      if (bLabel.toLowerCase() > aLabel.toLowerCase()) return 1;
    }
    return 0;
  });
  return sortedResources;
};

/**
 * A helper function to manage cancellation of asynchronous promises
 * @param {*} promise     The promise to wrap
 * @param {*} reference   A reference that can be used to identify the promise 
 */
export const makeCancelable = (promise, reference) => {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      val => hasCanceled_ ? reject({ isCanceled: true, reference: reference }) : resolve(val),
      error => hasCanceled_ ? reject({ isCanceled: true, reference: reference }) : reject(error)
    );
  });

  return {
    promise: wrappedPromise,
    isCancelled() { return hasCanceled_; },
    cancel() {
      hasCanceled_ = true;
    },
  };
};

/**
 * We don't allow leading spaces or tabs or other chunk
 * @param {*} text 
 */
export const sanitiseText = (text) => {
  if (!text) return "";
  text = text.trimLeft();
  text = text.replace(/\t/g, "");
  text = text.replace(/\n/g, "");
  text = text.replace(/\r/g, "");
  text = text.replace(/\f/g, "");
  return text;
};

export const lightSanitiseText = (text) => {
  if (!text) return "";
  text = text.trimLeft();
  text = text.replace(/\t/g, "");
  text = text.replace(/\r/g, "");
  text = text.replace(/\f/g, "");
  return text;
};

export const fullySanitiseText = (text) => {
  if (!text) return "";
  text = text.trimLeft();
  text = text.replace(/\t/g, "");
  text = text.replace(/\n/g, "");
  text = text.replace(/\r/g, "");
  text = text.replace(/\f/g, "");
  text = text.replace(/\\/g, "");
  text = text.replace(/"/g, "");
  text = text.replace(/'/g, "");
  return text;
};

// Allow a user to open a link in a new browser tab
export function getNewTabLink(link) {
  const tabLink = "?graphologi-target=" + GS.getActiveSubscriptionBucket() + "&subscription=" + GS.getActiveSubscription() + "#";
  const withTabLink1 = link.substring(link, link.indexOf("#"));
  const withTabLink2 = link.substring(link.indexOf("#") + 1);
  const fullTabLink = withTabLink1 + tabLink + withTabLink2;
  window.open(window.location.origin + "/gs" + fullTabLink);
}

export function isModuleActivated(modules, expected) {
  const flag = modules & expected;
  return flag === expected;
}

export function computeStatements(statements, property, value) {
  let applicableStatements = [];
  for (let data of statements) {
    if (data.rdfPredicate === property && "rdfObject" in data && data.rdfObject !== undefined) {
      // id maybe in id or @id form on dataObject
      if (data.rdfObject.id && value.id && data.rdfObject.id === value.id) {
        applicableStatements.push(data);
      }
      else if (data.rdfObject["@id"] && value.id && data.rdfObject["@id"] === value.id) {
        applicableStatements.push(data);
      }
      else if (data.rdfObject["@value"] && data.rdfObject["@value"] === value["@value"]) {
        if (data.rdfObject.type && data.rdfObject.type === value.type) {
          applicableStatements.push(data);
        }
      }
      else if (typeof data.rdfObject === 'boolean' && data.rdfObject === Boolean(value["@value"])) {
        applicableStatements.push(data);
      }
      else if (typeof data.rdfObject === 'number' && data.rdfObject === Number(value["@value"])) {
        applicableStatements.push(data);
      }
      else if (typeof data.rdfObject === 'string' && data.rdfObject === value["@value"]) {
        applicableStatements.push(data);
      }
    }
  }
  return applicableStatements;
}

export function computeRange(range) {
  return range && range.length > 0 ?
    typeof range[0] === "string" && !range[0].startsWith(BN_NAMESPACE) ?
      range[0]
      :
      undefined
    :
    undefined;
}

export async function deleteBlankNode(id, graph) {
  let bNodes = [];
  await computeBlankNodes(id, graph, bNodes);
  GS.deleteResource(id, graph);
  bNodes.forEach(bNode => GS.deleteResource(bNode, graph));
}

const bnProps = ["intersectionOf", "unionOf", "complementOf", "oneOf", "withRestrictions", "distinctMembers", "members",
  "someValuesFrom", "allValuesFrom", "onProperty", "onClass", "onDataRange"];

async function computeBlankNodes(id, graph, bNodes) {
  await GS.loadData([id], {}, graph);
  if (GS.isLoaded(id, graph)) {
    const data = GS.data(id, graph);
    for (let prop of bnProps) {
      const items = data[prop];
      if (items) {
        const arrayItems = Array.isArray(items) ? items : [items];
        for (let i of arrayItems) {
          if (typeof i === "string" && i.startsWith(BN_NAMESPACE)) {
            bNodes.push(i);
            await computeBlankNodes(i, graph, bNodes);
          }
        }
      }
    }
  }
}

export function domainOrRange(iris) {
  let results = [];
  let sortedIRIs = [...iris].sort();
  if (sortedIRIs.some(iri => iri.startsWith(BN_NAMESPACE))) {
    results.push("Complex");
  }
  else {
    for (let i of sortedIRIs) {
      let displayText = i;
      if (i.startsWith("http://www.w3.org/2001/XMLSchema#")) {
        displayText = "XSD " + i.substring(33);
      }
      else if (i.startsWith("http://www.w3.org/1999/02/22-rdf-syntax-ns#")) {
        displayText = "RDF " + i.substring(43);
      }
      else if (i.startsWith("http://www.w3.org/2004/02/skos/core#")) {
        displayText = "SKOS " + i.substring(36);
      }
      else if (i.startsWith("http://www.w3.org/2008/05/skos-xl#")) {
        displayText = "SKOS " + i.substring(34);
      }
      results.push(displayText);
    }
  }
  return results.join(", ");
}